import { unlockScroll } from '@theme/utilities';

/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 *
 * Mountain Line mobile hardening:
 * - reconcile stale iOS scroll locks after drawers/dialogs close or a page is restored.
 */

function releaseStaleScrollLocks() {
  const hasOpenLockingUi = Boolean(document.querySelector('dialog[open], details[scroll-lock][open]'));
  const mountainOverlay = document.querySelector('.mlx-easter');
  const openMountainOverlay = mountainOverlay && !mountainOverlay.hasAttribute('hidden');

  if (hasOpenLockingUi || openMountainOverlay) return;

  document.querySelectorAll('dialog, details[scroll-lock]').forEach((owner) => unlockScroll(owner));
  document.documentElement.removeAttribute('scroll-lock');

  // Older cached Mountain Line homepage code used an inline body overflow lock.
  // Clear it only when no Mountain Line overlay is open.
  if (mountainOverlay?.hasAttribute('hidden') && document.body?.style.overflow === 'hidden') {
    document.body.style.overflow = '';
  }
}

function scheduleScrollLockReconcile() {
  requestAnimationFrame(() => requestAnimationFrame(releaseStaleScrollLocks));
}

function initMobileStability() {
  window.addEventListener('pageshow', scheduleScrollLockReconcile);
  window.addEventListener('focus', scheduleScrollLockReconcile);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) scheduleScrollLockReconcile();
  });
  document.addEventListener('theme-drawer:close', scheduleScrollLockReconcile);
  document.addEventListener('dialog:close', scheduleScrollLockReconcile);
  document.addEventListener('toggle', (event) => {
    if (event.target instanceof HTMLDetailsElement && event.target.hasAttribute('scroll-lock') && !event.target.open) {
      scheduleScrollLockReconcile();
    }
  }, {capture:true});

  scheduleScrollLockReconcile();
}

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

initMobileStability();

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
